import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

const SubscribeInput = z.object({
  domain: z.string().trim().min(3).max(255),
  email: z.string().trim().email().max(255),
  brand: z.string().trim().max(200).optional(),
});

const TokenInput = z.object({ token: z.string().trim().min(8).max(128) });

export type ScoreWatchResult =
  | { status: "ok"; alreadyTracking: boolean }
  | { status: "error"; message: string };

/**
 * Opt in to score-change tracking for a domain. Deliberately separate from the
 * "unlock full report" email capture: no onboarding handoff, no Lead411 drip.
 */
export const subscribeScoreWatch = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => SubscribeInput.parse(input))
  .handler(async ({ data }): Promise<ScoreWatchResult> => {
    const { normalizeDomain } = await import("./ai-readiness.server");
    const domain = normalizeDomain(data.domain);
    if (!domain) return { status: "error", message: "Enter a valid domain." };

    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const email = data.email.toLowerCase();

    const existing = await supabaseAdmin
      .from("score_watchers")
      .select("id, status")
      .eq("domain", domain)
      .ilike("email", email)
      .maybeSingle();

    if (existing.data) {
      if (existing.data.status !== "active") {
        await supabaseAdmin
          .from("score_watchers")
          .update({
            status: "active",
            next_scan_at: new Date(Date.now() + 14 * 86400_000).toISOString(),
          })
          .eq("id", existing.data.id);
      }
      return { status: "ok", alreadyTracking: true };
    }

    // Seed the baseline from the most recent readiness scan for this domain so
    // the first email can show a real delta.
    const baseline = await supabaseAdmin
      .from("ai_readiness_checks")
      .select("score, tier, breakdown")
      .eq("domain", domain)
      .not("score", "is", null)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();

    const ins = await supabaseAdmin.from("score_watchers").insert({
      email,
      domain,
      brand: data.brand ?? null,
      last_score: baseline.data?.score ?? null,
      last_tier: baseline.data?.tier ?? null,
      last_breakdown: (baseline.data?.breakdown as any) ?? null,
      last_scanned_at: baseline.data ? new Date().toISOString() : null,
    });
    if (ins.error) return { status: "error", message: "Could not save. Try again." };
    return { status: "ok", alreadyTracking: false };
  });

export const unsubscribeScoreWatch = createServerFn({ method: "POST" })
  .inputValidator((input: unknown) => TokenInput.parse(input))
  .handler(async ({ data }): Promise<{ ok: boolean; email?: string }> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const r = await supabaseAdmin
      .from("score_watchers")
      .update({ status: "unsubscribed" })
      .eq("unsubscribe_token", data.token)
      .select("email")
      .maybeSingle();
    if (r.error || !r.data) return { ok: false };
    // One unsubscribe means out of every email channel, not just this one.
    const { suppressEmailEverywhere } = await import("./email/suppress.server");
    await suppressEmailEverywhere(supabaseAdmin as any, r.data.email as string);
    return { ok: true, email: r.data.email as string };
  });

